const MILLISECONDS_PER_SECOND = 1_000


/**
 * Holds an access token for the lifetime of the page.
 *
 * Memory only, deliberately: the token is a short-lived credential, so
 * writing it to `sessionStorage` / `localStorage` would widen its exposure
 * for no benefit. A page reload drops it and the app acquires a new one.
 *
 * Consumers hold an instance rather than React state because the ones that
 * need the token most, the Axios interceptors, live outside the React tree.
 * `subscribe()` exists so React components can still re-render on a change.
 *
 * Two things to know before subscribing. Notification fires on every
 * `setAccessToken()` and `clear()` call, whether or not the stored value
 * actually differs, so subscribers must tolerate being told nothing new.
 * And subscribers are not isolated from each other: one that throws
 * prevents the rest from running. That is deliberate, so a broken
 * subscriber fails loudly instead of degrading the others silently.
 */
export default class AccessTokenStore {
  constructor() {
    this._accessToken = null
    this._expiresAt = null
    this._generation = 0
    this._subscribers = new Set()
  }

  /**
   * The token is returned exactly as it was given, expired or not. Truthiness
   * therefore tells a caller only that a token is held, never that it is still
   * usable: `isAuthenticated()` answers that. Prefer truthiness to a `null`
   * comparison all the same, since a token that arrived empty is no token.
   *
   * @returns {String|null} the token held, if any
   */
  getAccessToken() {
    return this._accessToken
  }

  /**
   * Measured against the clock rather than counted down, so the value is
   * fractional and shrinks between calls.
   *
   * @returns {Number|null} seconds of life left in the token held, negative
   *   once it has expired, `null` when no token is held, or `NaN` when the
   *   lifetime it was given could not be read as a number
   */
  getExpiresIn() {
    if ( !this._accessToken ) {
      return null
    }

    return ( this._expiresAt - Date.now() ) / MILLISECONDS_PER_SECOND
  }

  /**
   * Counts the `clear()` calls made so far, so that work started against one
   * token can tell whether the store was emptied out from under it while it
   * ran. Replacing one token with another does not move the count: nothing was
   * torn down.
   *
   * @returns {Number} how many times `clear()` has run
   */
  getGeneration() {
    return this._generation
  }

  /**
   * A token that expires while it sits here goes from usable to unusable
   * with no notification: only `setAccessToken()` and `clear()` notify.
   * Anything that needs to act at the moment of expiry has to schedule
   * against `getExpiresIn()` rather than wait to be told.
   *
   * @returns {Boolean} whether a usable token is held
   */
  isAuthenticated() {
    return Boolean(this._accessToken) && ( Date.now() < this._expiresAt )
  }

  /**
   * Records a newly-issued token and notifies subscribers.
   *
   * The lifetime is taken from the issuing response rather than from the
   * token itself, so that a client clock offset from the server's can't make
   * a live token look dead (or a dead one look live). A clock that jumps
   * mid-session, though, does move the deadline it computes here.
   *
   * The token is stored as given. The lifetime is not: it becomes an absolute
   * deadline computed on receipt, and the seconds figure itself is discarded.
   *
   * An `expiresIn` that is absent or unparseable makes that deadline `NaN`.
   * Every comparison against `NaN` is false, so `isAuthenticated()` reports an
   * unusable token rather than granting access, but note that it fails closed
   * for that reason and not because the deadline sits in the past.
   * `getExpiresIn()` returns `NaN` in the same case, so anything scheduling
   * against it needs to check that what it got is finite.
   *
   * @param {Object} args
   * @param {String} args.accessToken
   * @param {Number} args.expiresIn - seconds of life remaining, as reported
   *   by the response that issued the token
   */
  setAccessToken({ accessToken, expiresIn }) {
    this._accessToken = accessToken
    this._expiresAt = Date.now() + ( expiresIn * MILLISECONDS_PER_SECOND )

    this._notifySubscribers()
  }

  /**
   * Discards the token held and notifies subscribers, whether or not a token
   * was actually held, and advances the generation so that work already
   * running against the token discarded can tell its result is unwanted.
   */
  clear() {
    this._accessToken = null
    this._expiresAt = null
    this._generation += 1

    this._notifySubscribers()
  }

  /**
   * @param {Function} subscriber - invoked with no arguments on every
   *   `setAccessToken()` and `clear()` call
   *
   * @returns {Function} invoke to unsubscribe
   */
  subscribe(subscriber) {
    this._subscribers.add(subscriber)

    return () => {
      this._subscribers.delete(subscriber)
    }
  }

  _notifySubscribers() {
    this._subscribers.forEach((subscriber) => {
      subscriber()
    })
  }
}
